// WidgetPreview.tsx — the widget's live-preview iframe (srcdoc + `data-config` override +
// debounce), extracted from `BotAppearance.tsx` (M5 task-5/spec D6) so the onboarding
// wizard's Look step can reuse the exact same logic instead of a second copy drifting out
// of sync. Zero behavior change at the BotAppearance call site.
import { useEffect, useMemo, useState } from 'react';
import type { BotLanguageSetting } from '@repo/shared-types';
import { useT } from '../locales/context';

export interface WidgetPreviewProps {
  publicId: string;
  color?: string;
  botName?: string;
  welcome?: string;
  quickQuestions?: string[];
  /** Badge opt-in spec (2026-08-11) — the EFFECTIVE value (`resolveShowsBadge`'s result, not
   * either input alone), same field name as `PublicBotConfigDto.showsBadge` so it rides
   * through untouched in the override below. `undefined` (not sent at all — see the filter
   * below) lets the server's own fetched config decide, for callers that don't yet know the
   * plan (BotAppearance while its billing-summary query is still pending). */
  showsBadge?: boolean;
  /** Widget-i18n spec, Task 3 — threaded straight into `data-config` like every other
   * appearance field here; `apps/widget/src/config-override.ts`'s allowlist is what makes the
   * override actually take effect (validated against the real locale union there). Omitted
   * entirely (not just falsy) when unset, so callers that don't pass it — e.g. the onboarding
   * wizard's `LookStep` — get the server's own default instead of forcing `undefined` through. */
  language?: BotLanguageSetting;
  /** iframe height in px — defaults to the size BotAppearance always used. */
  height?: number;
  /** Bump to force the iframe to remount (BotAppearance does this right after a successful
   * save so the just-persisted server-side defaults show through cleanly). */
  remountKey?: number;
}

const DEFAULT_HEIGHT = 520;
const DEBOUNCE_MS = 250;

// Cache-buster for the preview's `/widget.js`. That URL is a Pages Function proxy to the API,
// so the bundle sits behind TWO edge caches (dashboard origin + API origin), each allowed
// `max-age=300, stale-while-revalidate=3600` — right after a widget deploy the preview can run
// a build up to an hour older than the one real sites load from the API directly (found
// 2026-08-21: pinning a freshly shipped locale did nothing in the preview while the live
// widget honoured it). The preview exists to show the CURRENT widget, so its cache key changes
// once per dashboard page load: fresh on every visit, still browser-cached across the
// keystroke-driven srcdoc remounts within that visit.
const PREVIEW_SCRIPT_SRC = `/widget.js?v=${Date.now()}`;

export function WidgetPreview({
  publicId,
  color,
  botName,
  welcome,
  quickQuestions,
  showsBadge,
  language,
  height = DEFAULT_HEIGHT,
  remountKey = 0,
}: WidgetPreviewProps) {
  // Locale-dashboard plan, Task 11 — resolved via `useT()` directly rather than a `t` prop:
  // this component's only two callers (`BotAppearance.tsx`, onboarding's `LookStep.tsx`) both
  // already call `useT()` for their own copy, but the iframe `title` below is this component's
  // one and only string, so a whole prop for it isn't worth the extra plumbing at either call
  // site — same "no-provider default is English" idiom `chat-bubbles.tsx`'s `AssistantBubble`
  // already relies on.
  const t = useT();
  // Live preview re-renders on every keystroke (handoff §Appearance / D6 step 4) — current
  // prop values ride into the iframe as the widget's `data-config` override, debounced just
  // enough to not remount mid-word. HTML-escape the JSON for the attribute.
  const [debounced, setDebounced] = useState({
    color, botName, welcome, quickQuestions, showsBadge, language,
  });
  useEffect(() => {
    const t = setTimeout(
      () => setDebounced({ color, botName, welcome, quickQuestions, showsBadge, language }),
      DEBOUNCE_MS,
    );
    return () => clearTimeout(t);
  }, [color, botName, welcome, quickQuestions, showsBadge, language]);

  const srcDoc = useMemo(() => {
    // Empty strings are omitted rather than sent: the server's own fallbacks (e.g.
    // botName -> bot.name) should show through instead of being overridden by a blank.
    // `showsBadge` is a plain boolean (never `''`/`[]`), so `false` rides through unfiltered —
    // only `undefined` (unknown yet) is dropped, by the shared filter loop below.
    // `language` isn't string-empty-able ('auto'/'en'/'vi' are all non-empty), so it only
    // needs the shared undefined check below, not its own special case.
    const raw: Record<string, unknown> = {
      color: debounced.color,
      botName: debounced.botName?.trim(),
      welcome: debounced.welcome?.trim(),
      quickQuestions: debounced.quickQuestions?.filter((q) => q.trim() !== ''),
      showsBadge: debounced.showsBadge,
      language: debounced.language,
    };
    for (const k of Object.keys(raw)) {
      const v = raw[k];
      if (v === undefined || v === '' || (Array.isArray(v) && v.length === 0)) delete raw[k];
    }
    const override = JSON.stringify(raw).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
    return (
      `<!doctype html><html><head><meta charset="utf-8" /></head><body style="margin:0">` +
      `<script src="${PREVIEW_SCRIPT_SRC}" data-bot="${publicId}" data-config="${override}" async></script>` +
      `</body></html>`
    );
  }, [publicId, debounced]);

  return (
    <iframe
      key={remountKey}
      title={t.appearance.widgetPreviewTitle}
      sandbox="allow-scripts allow-same-origin"
      srcDoc={srcDoc}
      style={{ height }}
      className="w-full rounded-xl border bg-white"
    />
  );
}
